import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderSync, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import {
  createSourceConnection,
  deleteSourceConnection,
  getAclCoverage,
  getSourceConnections,
  testSourceConnection,
} from '@/api/sourceConnections';
import type { SourceConnection } from '@/api/sourceConnections';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const SOURCES_QUERY_KEY = ['source-connections'];

/** Per-provider config fields (identityProviderFields.ts pattern). */
export const SOURCE_TYPE_FIELDS: Record<
  string,
  { label: string; fields: { key: string; label: string; secret?: boolean }[] }
> = {
  gdrive: {
    label: 'Google Drive',
    fields: [
      { key: 'service_account_json', label: 'Service account JSON', secret: true },
      { key: 'folder_id', label: 'Folder ID (optional scope)' },
    ],
  },
  sharepoint: {
    label: 'SharePoint / OneDrive',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID' },
      { key: 'client_id', label: 'Client ID' },
      { key: 'client_secret', label: 'Client secret', secret: true },
      { key: 'drive_id', label: 'Drive ID' },
    ],
  },
  confluence: {
    label: 'Confluence Cloud',
    fields: [
      { key: 'base_url', label: 'Base URL (https://you.atlassian.net/wiki)' },
      { key: 'email', label: 'Account email' },
      { key: 'api_token', label: 'API token', secret: true },
      { key: 'space_key', label: 'Space key' },
    ],
  },
  notion: {
    label: 'Notion',
    fields: [{ key: 'api_token', label: 'Integration token', secret: true }],
  },
};

function CreateSourceDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState('gdrive');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createSourceConnection({ name, source_type: sourceType, config }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_QUERY_KEY });
      setName('');
      setConfig({});
      setError(null);
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const spec = SOURCE_TYPE_FIELDS[sourceType];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a document source</DialogTitle>
          <DialogDescription>
            Documents AND their permissions sync from the source. Credentials
            are envelope-encrypted with your org's KMS binding.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Connection name"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value);
              setConfig({});
            }}
            aria-label="Source type"
          >
            {Object.entries(SOURCE_TYPE_FIELDS).map(([key, s]) => (
              <option key={key} value={key}>
                {s.label}
              </option>
            ))}
          </select>
          {spec.fields.map((field) => (
            <Input
              key={field.key}
              type={field.secret ? 'password' : 'text'}
              autoComplete={field.secret ? 'new-password' : 'off'}
              placeholder={field.label}
              value={config[field.key] ?? ''}
              onChange={(e) =>
                setConfig((c) => ({ ...c, [field.key]: e.target.value }))
              }
            />
          ))}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !name}>
            {create.isPending ? 'Connecting...' : 'Connect source'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CoveragePanel({ sourceId }: { sourceId: string }) {
  const { data } = useQuery({
    queryKey: ['acl-coverage', sourceId],
    queryFn: () => getAclCoverage(sourceId),
  });
  if (!data) return null;
  return (
    <div className="mt-3 rounded-md border p-3 text-sm">
      <p className="mb-1 flex items-center gap-1 font-medium">
        <ShieldCheck className="h-4 w-4" /> Imported ACL coverage
      </p>
      <p className="text-muted-foreground">
        {data.total_entries} entries: {data.matched_users} matched users,{' '}
        {data.unmatched_users} unmatched, {data.group_entries} groups,{' '}
        {data.open_access_entries} open-access.
      </p>
      {data.unmatched_user_emails.length > 0 && (
        <p className="mt-1 text-amber-600 dark:text-amber-400">
          Unknown to Gateco (no synced principal):{' '}
          {data.unmatched_user_emails.join(', ')}
        </p>
      )}
    </div>
  );
}

function SourceCard({ source }: { source: SourceConnection }) {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<string | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);

  const test = useMutation({
    mutationFn: () => testSourceConnection(source.id),
    onSuccess: (r) => setTestResult(r.message),
    onError: (e: Error) => setTestResult(e.message),
  });
  const remove = useMutation({
    mutationFn: () => deleteSourceConnection(source.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SOURCES_QUERY_KEY }),
  });

  const typeLabel = SOURCE_TYPE_FIELDS[source.source_type]?.label ?? source.source_type;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-base">{source.name}</CardTitle>
          <CardDescription>
            {typeLabel}
            {source.last_synced_at
              ? ` · last synced ${new Date(source.last_synced_at).toLocaleString()}`
              : ' · never synced'}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => test.mutate()}>
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCoverage((v) => !v)}
          >
            Coverage
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => remove.mutate()}
            aria-label={`Delete ${source.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Attach this source to a pipeline (set its source connection) and run
          the pipeline to sync documents and permissions.
        </p>
        {testResult && (
          <p className="mt-2 text-sm text-muted-foreground">{testResult}</p>
        )}
        {showCoverage && <CoveragePanel sourceId={source.id} />}
      </CardContent>
    </Card>
  );
}

export function SourcesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: sources, isLoading } = useQuery({
    queryKey: SOURCES_QUERY_KEY,
    queryFn: getSourceConnections,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document Sources"
        subtitle="Sync documents and their permissions from Google Drive, SharePoint, Confluence, and Notion."
      />
      <div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Connect source
        </Button>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (sources?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <FolderSync className="mx-auto mb-2 h-8 w-8" />
            No document sources connected yet. Connect one to sync documents
            AND their permissions into Gateco.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sources?.map((s) => (
            <SourceCard key={s.id} source={s} />
          ))}
        </div>
      )}
      <CreateSourceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}

export default SourcesPage;
